import styled, { css } from 'styled-components';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps {
  $variant?: Variant;
  $size?: 'sm' | 'md' | 'lg';
  $block?: boolean;
}

export const Button = styled.button<ButtonProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(2)};
  width: ${({ $block }) => ($block ? '100%' : 'auto')};
  font-family: inherit;
  font-weight: 600;
  letter-spacing: 0.01em;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  white-space: nowrap;
  transform: translateZ(0);
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    border-color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    box-shadow ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.easeOut},
    transform ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:active:not(:disabled) {
    transform: translateY(1px) scale(0.99);
  }

  ${({ $size = 'md', theme }) => {
    switch ($size) {
      case 'sm':
        return css`
          padding: ${theme.spacing(1.5)} ${theme.spacing(3)};
          font-size: 13px;
          height: 32px;
        `;
      case 'lg':
        return css`
          padding: ${theme.spacing(3.5)} ${theme.spacing(6)};
          font-size: 15px;
          height: 48px;
        `;
      default:
        return css`
          padding: ${theme.spacing(2.5)} ${theme.spacing(4)};
          font-size: 14px;
          height: 40px;
        `;
    }
  }}

  ${({ $variant = 'primary', theme }) => {
    switch ($variant) {
      case 'primary':
        return css`
          background: ${theme.colors.primary};
          color: #fff;
          box-shadow: ${theme.shadow.xs};
          &:hover:not(:disabled) {
            background: ${theme.colors.primaryHover};
            box-shadow: ${theme.shadow.primary};
            transform: translateY(-1px);
          }
        `;
      case 'secondary':
        return css`
          background: ${theme.colors.backgroundElevated};
          color: ${theme.colors.text.primary};
          border-color: ${theme.colors.border};
          &:hover:not(:disabled) {
            background: ${theme.colors.surfaceHover};
            border-color: ${theme.colors.borderStrong};
          }
        `;
      case 'ghost':
        return css`
          background: transparent;
          color: ${theme.colors.text.secondary};
          &:hover:not(:disabled) {
            background: ${theme.colors.surface};
            color: ${theme.colors.text.primary};
          }
        `;
      case 'danger':
        return css`
          background: ${theme.colors.status.danger};
          color: #fff;
          &:hover:not(:disabled) {
            filter: brightness(1.1);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
